/**
 * Mes Bulletins de Paie — Employé
 */
import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { FileText, Download, Wallet, TrendingUp, Receipt, Eye } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';

const fmt = (n, c = 'XOF') => `${(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} ${c}`;

function PayslipDetail({ payslip, open, onClose }) {
  if (!payslip) return null;
  const gains = (payslip.lines || []).filter(l => l.kind === 'GAIN');
  const employeeContribs = (payslip.lines || []).filter(l => l.kind === 'EMPLOYEE_CONTRIBUTION');
  const deductions = (payslip.lines || []).filter(l => l.kind === 'DEDUCTION');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="payslip-detail">
        <DialogHeader>
          <DialogTitle>Bulletin de paie — {payslip.period}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Gains */}
          <div>
            <h3 className="font-bold text-sm uppercase text-emerald-700 mb-2">Gains</h3>
            <table className="w-full text-sm">
              <tbody>
                {gains.map(l => (
                  <tr key={l.code} className="border-b">
                    <td className="py-1.5">{l.name}</td>
                    <td className="py-1.5 text-right font-medium">{fmt(l.amount, payslip.currency)}</td>
                  </tr>
                ))}
                <tr className="font-bold border-t-2">
                  <td className="py-2">Salaire brut</td>
                  <td className="py-2 text-right">{fmt(payslip.grossSalary, payslip.currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Retenues */}
          {(employeeContribs.length > 0 || deductions.length > 0) && (
            <div>
              <h3 className="font-bold text-sm uppercase text-amber-700 mb-2">Retenues</h3>
              <table className="w-full text-sm">
                <tbody>
                  {employeeContribs.map(l => (
                    <tr key={l.code} className="border-b">
                      <td className="py-1.5">{l.name}</td>
                      <td className="py-1.5 text-right text-red-600">-{fmt(l.amount, payslip.currency)}</td>
                    </tr>
                  ))}
                  {deductions.map(l => (
                    <tr key={l.code} className="border-b">
                      <td className="py-1.5">{l.name}</td>
                      <td className="py-1.5 text-right text-red-600">-{fmt(l.amount, payslip.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Net */}
          <div className="bg-emerald-600 text-white p-4 rounded-lg flex items-center justify-between">
            <span className="font-bold text-lg">NET À PAYER</span>
            <span className="font-bold text-2xl">{fmt(payslip.netToPay, payslip.currency)}</span>
          </div>

          {/* Info charges patronales */}
          <div className="text-xs text-slate-500 italic border-t pt-3">
            Charges patronales (information) : {fmt(payslip.employerCharges, payslip.currency)} —
            Coût total employeur : {fmt(payslip.grossSalary + payslip.employerCharges, payslip.currency)}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MyPayslipsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/payroll/my/payslips');
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (ps) => {
    try {
      const res = await api.get(`/payroll/payslips/${ps.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulletin-${ps.period}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Téléchargement échoué');
    }
  };

  // KPI: 12 derniers mois
  const last12 = items.slice(0, 12);
  const totalNet = last12.reduce((s, p) => s + (p.netToPay || 0), 0);
  const avgNet = last12.length ? totalNet / last12.length : 0;
  const currency = items[0]?.currency || 'XOF';

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="my-payslips-page">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Wallet className="w-7 h-7 text-indigo-600" /> Mes bulletins de paie
          </h1>
          <p className="text-slate-500 mt-1">Téléchargez vos bulletins certifiés (PDF avec QR & signature).</p>
        </div>

        {/* KPIs */}
        {items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <Receipt className="w-8 h-8 text-indigo-500" />
                <div>
                  <div className="text-xs text-slate-500">Bulletins disponibles</div>
                  <div className="text-2xl font-bold">{items.length}</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-emerald-500" />
                <div>
                  <div className="text-xs text-slate-500">Net moyen (12 derniers mois)</div>
                  <div className="text-xl font-bold">{fmt(avgNet, currency)}</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <Wallet className="w-8 h-8 text-blue-500" />
                <div>
                  <div className="text-xs text-slate-500">Cumul 12 mois</div>
                  <div className="text-xl font-bold">{fmt(totalNet, currency)}</div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Card>
          <CardHeader><CardTitle>Historique</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-slate-500">Chargement…</p>
            ) : items.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <FileText className="w-12 h-12 mx-auto text-slate-300" />
                <p className="mt-3 text-slate-600 font-medium">Aucun bulletin disponible</p>
                <p className="text-sm text-slate-400 mt-1">Vos bulletins apparaîtront ici une fois la paie traitée.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {items.map(p => (
                  <div key={p.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition"
                    data-testid={`my-payslip-${p.period}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div>
                        <div className="font-bold">Bulletin {p.period}</div>
                        <div className="text-sm text-slate-500">
                          Brut: {fmt(p.grossSalary, p.currency)} • Net:{' '}
                          <span className="font-medium text-emerald-700">{fmt(p.netToPay, p.currency)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setDetail(p)}
                        data-testid={`view-payslip-${p.period}`}>
                        <Eye className="w-4 h-4 mr-1" /> Détail
                      </Button>
                      <Button size="sm" onClick={() => downloadPdf(p)}
                        data-testid={`download-payslip-${p.period}`}>
                        <Download className="w-4 h-4 mr-1" /> PDF
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <PayslipDetail payslip={detail} open={!!detail} onClose={() => setDetail(null)} />
      </div>
    </DashboardLayout>
  );
}
